/**
 * TanStack Query key factories. Centralizing keys here keeps cache reads and
 * invalidations consistent — a mutation invalidates `loanKeys.lists()` and
 * every list query rooted at it is refreshed, with no stringly-typed drift.
 */
import type { OutboundEmailCategory } from '@lib/enums/outbound-email.enum'

export const companyKeys = {
  all: ['companies'] as const,
  lists: () => [...companyKeys.all, 'list'] as const,
  list: () => [...companyKeys.lists()] as const,
  details: () => [...companyKeys.all, 'detail'] as const,
  detail: (id: string) => [...companyKeys.details(), id] as const,
  members: (id: string) => [...companyKeys.detail(id), 'members'] as const,
  invitations: (id: string) =>
    [...companyKeys.detail(id), 'invitations'] as const,
  loanPrograms: (id: string) =>
    [...companyKeys.detail(id), 'loan-programs'] as const,
  underwritingGuidelines: (id: string) =>
    [...companyKeys.detail(id), 'underwriting-guidelines'] as const,
  trustees: (id: string) => [...companyKeys.detail(id), 'trustees'] as const,
  defaultFees: (id: string) =>
    [...companyKeys.detail(id), 'default-fees'] as const,
  attorneys: (id: string) =>
    [...companyKeys.detail(id), 'attorneys'] as const,
  escrowPolicies: (id: string) =>
    [...companyKeys.detail(id), 'escrow-policies'] as const,
  underwritingRules: (id: string) =>
    [...companyKeys.detail(id), 'underwriting-rules'] as const,
}

export const attorneyKeys = {
  all: ['admin', 'attorneys'] as const,
  list: () => [...attorneyKeys.all] as const,
}

export const servicerKeys = {
  all: ['admin', 'servicers'] as const,
  list: () => [...servicerKeys.all] as const,
}

export const titleCompanyKeys = {
  all: ['title-companies'] as const,
  list: () => [...titleCompanyKeys.all, 'list'] as const,
}

export const loanKeys = {
  all: ['loans'] as const,
  lists: () => [...loanKeys.all, 'list'] as const,
  list: () => [...loanKeys.lists()] as const,
  details: () => [...loanKeys.all, 'detail'] as const,
  detail: (id: string) => [...loanKeys.details(), id] as const,
  programs: (id: string) => [...loanKeys.detail(id), 'programs'] as const,
  attorneys: (id: string) => [...loanKeys.detail(id), 'attorneys'] as const,
  trustees: (id: string) => [...loanKeys.detail(id), 'trustees'] as const,
  portalAccess: (id: string) =>
    [...loanKeys.detail(id), 'portal-access'] as const,
  issues: (id: string) => [...loanKeys.detail(id), 'issues'] as const,
  underwritingSummary: (id: string) =>
    [...loanKeys.detail(id), 'underwriting-summary'] as const,
  tolerance: (id: string) => [...loanKeys.detail(id), 'tolerance'] as const,
  conditions: (id: string) => [...loanKeys.detail(id), 'conditions'] as const,
  adverseAction: (id: string) =>
    [...loanKeys.detail(id), 'adverse-action'] as const,
  subjectProperty: (id: string) =>
    [...loanKeys.detail(id), 'subject-property'] as const,
  appraisals: (id: string) =>
    [...loanKeys.detail(id), 'appraisals'] as const,
  appraisalReview: (loanId: string, appraisalId: string) =>
    [...loanKeys.detail(loanId), 'appraisals', appraisalId, 'review'] as const,
  audit: (id: string) => [...loanKeys.detail(id), 'audit'] as const,
  documents: (id: string) => [...loanKeys.detail(id), 'documents'] as const,
  hoa: (id: string) => [...loanKeys.detail(id), 'hoa'] as const,
  contract: (id: string) => [...loanKeys.detail(id), 'contract'] as const,
  contractExtractJobs: (id: string) =>
    [...loanKeys.detail(id), 'contract', 'extract-jobs'] as const,
  title: (id: string) => [...loanKeys.detail(id), 'title'] as const,
  vestingDeedExtractJobs: (id: string) =>
    [...loanKeys.detail(id), 'title', 'extract-jobs'] as const,
  titleCommitments: (id: string) =>
    [...loanKeys.detail(id), 'title', 'commitments'] as const,
  insurance: (id: string) => [...loanKeys.detail(id), 'insurance'] as const,
  legal: (id: string) => [...loanKeys.detail(id), 'legal'] as const,
  attorneyPackage: (id: string) =>
    [...loanKeys.detail(id), 'legal', 'attorney-package'] as const,
  outboundEmails: (id: string, category?: OutboundEmailCategory) =>
    [...loanKeys.detail(id), 'outbound-emails', category ?? 'all'] as const,
  outboundEmail: (id: string, emailId: string) =>
    [...loanKeys.detail(id), 'outbound-email', emailId] as const,
  titleRequest: (id: string) =>
    [...loanKeys.detail(id), 'contract', 'title-request'] as const,
  preApproval: (id: string) =>
    [...loanKeys.detail(id), 'pre-approval'] as const,
  borrowers: (id: string) => [...loanKeys.detail(id), 'borrowers'] as const,
  borrower: (loanId: string, borrowerId: string) =>
    [...loanKeys.detail(loanId), 'borrowers', borrowerId] as const,
  borrowerW2s: (loanId: string, borrowerId: string) =>
    [...loanKeys.borrower(loanId, borrowerId), 'w2s'] as const,
  borrowerPaystubs: (loanId: string, borrowerId: string) =>
    [...loanKeys.borrower(loanId, borrowerId), 'paystubs'] as const,
  borrowerPaystubExtractJobs: (loanId: string, borrowerId: string) =>
    [...loanKeys.borrower(loanId, borrowerId), 'paystub-extract-jobs'] as const,
  borrowerForm1099s: (loanId: string, borrowerId: string) =>
    [...loanKeys.borrower(loanId, borrowerId), 'form-1099s'] as const,
  borrowerTaxReturns: (loanId: string, borrowerId: string) =>
    [...loanKeys.borrower(loanId, borrowerId), 'tax-returns'] as const,
  assets: (id: string) => [...loanKeys.detail(id), 'assets'] as const,
  liabilities: (id: string) =>
    [...loanKeys.detail(id), 'liabilities'] as const,
  realEstate: (id: string) =>
    [...loanKeys.detail(id), 'real-estate'] as const,
  gifts: (id: string) => [...loanKeys.detail(id), 'gifts'] as const,
  otherLiabilities: (id: string) =>
    [...loanKeys.detail(id), 'other-liabilities'] as const,
  reserves: (id: string) => [...loanKeys.detail(id), 'reserves'] as const,
  underwritingFindings: (id: string) =>
    [...loanKeys.detail(id), 'underwriting-findings'] as const,
  loanEstimate: (id: string) =>
    [...loanKeys.detail(id), 'loan-estimate'] as const,
  loanEstimates: (id: string) =>
    [...loanKeys.detail(id), 'loan-estimates'] as const,
  loanEstimateVersion: (loanId: string, estimateId: string) =>
    [...loanKeys.loanEstimate(loanId), estimateId] as const,
  loanEstimateSignature: (loanId: string, estimateId: string) =>
    [...loanKeys.loanEstimateVersion(loanId, estimateId), 'signature'] as const,
  compliance: (id: string) =>
    [...loanKeys.detail(id), 'compliance'] as const,
  closingDisclosure: (id: string) =>
    [...loanKeys.detail(id), 'closing-disclosure'] as const,
  closingDisclosures: (id: string) =>
    [...loanKeys.detail(id), 'closing-disclosures'] as const,
  closingDisclosureVersion: (loanId: string, disclosureId: string) =>
    [...loanKeys.closingDisclosure(loanId), disclosureId] as const,
  incomeDocumentation: (id: string) =>
    [...loanKeys.detail(id), 'income-documentation'] as const,
  creditReports: (id: string) =>
    [...loanKeys.detail(id), 'credit-reports'] as const,
  creditReportExtractJobs: (id: string) =>
    [...loanKeys.detail(id), 'credit-report-extract-jobs'] as const,
  /** These omit the `detail` segment. Inserting it would miss the live cache. */
  bankAccounts: (loanId: string, borrowerId: string) =>
    ['loans', loanId, 'borrowers', borrowerId, 'bank-accounts'] as const,
  bankExtractJobs: (loanId: string, borrowerId: string) =>
    ['loans', loanId, 'borrowers', borrowerId, 'bank-extract-jobs'] as const,
  bankStatements: (loanId: string, borrowerId: string, accountId: string) =>
    [...loanKeys.bankAccounts(loanId, borrowerId), accountId, 'statements'] as const,
  bankTransactions: (
    loanId: string,
    borrowerId: string,
    accountId: string,
    statementId: string,
  ) =>
    [
      ...loanKeys.bankStatements(loanId, borrowerId, accountId),
      statementId,
      'transactions',
    ] as const,
}
